import constants from '../assets/constants';

/**
 * ── ONE PLACE THAT OWNS `<html lang>` AND `<html dir>` ───────────────────────────────────────────
 *
 * These two attributes used to be written from two independent copies of the same logic — the
 * pre-mount boot block in `main.tsx` (reading the persisted redux locale) and an effect in
 * `App.tsx` (reading redux `locale` / `i18n.language`). Both were correct in isolation and both
 * were EFFECT-shaped, so the document could sit on the previous language for the width of an
 * i18next namespace fetch, and a switch back to `en` was observed leaving `lang="ar"` behind.
 *
 * Everything now goes through `applyDocumentLocale`, and the language switcher calls it
 * SYNCHRONOUSLY in the click handler — before any await, any dispatch effect, any i18next
 * resolution — so the document can never lag the user's choice. The effects still call it too;
 * it is idempotent and cheap.
 */

/** `en-GB` → `en`. Empty / missing input falls back to `en`, matching i18n.js's `fallbackLng`. */
export const resolveLocaleBase = (lang?: string | null): string =>
  String(lang || 'en').toLowerCase().split('-')[0];

/** Point `<html lang>`, `<html dir>` and `<body dir>` at `lang`. Safe to call repeatedly. */
export const applyDocumentLocale = (lang?: string | null): string => {
  const base = resolveLocaleBase(lang);
  if (typeof document === 'undefined') return base;
  const dir = constants.rtlLangs.indexOf(base) > -1 ? 'rtl' : 'ltr';
  const html = document.documentElement;
  if (html.getAttribute('dir') !== dir) html.setAttribute('dir', dir);
  // Set `lang` UNCONDITIONALLY on a mismatch, in both directions. The bug this fixes was
  // one-directional: en → ar updated it, ar → en did not, leaving screen readers and
  // `:lang()` CSS announcing Arabic over English content.
  if (html.getAttribute('lang') !== base) html.setAttribute('lang', base);
  if (document.body && document.body.getAttribute('dir') !== dir) {
    document.body.setAttribute('dir', dir);
  }
  return base;
};

export default applyDocumentLocale;
